import * as React from 'react';
import classNames from 'classnames';
import s from '../PlansGrid.scss';
import { AdvancedPlansProductData } from 'types';
import { Text } from 'wix-style-react';
import { PriceWithDecimal } from '../PriceWithDecimal';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import { SelectButton } from './SelectButton';
import { LoggedIn } from 'components/LoggedIn/LoggedIn';
import { ProductHat } from '../ProductHat/ProductHat';
import { ProductNotes } from '../ProductNotes/ProductNotes';
import { withInjectedRootStore, RootStore } from 'providers';
import { useTranslation } from 'react-i18next';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { Experiments, Language } from 'enums';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';

export interface ProductHeaderProps extends InjectedExperimentsProps {
  product: AdvancedPlansProductData;
  onClickSelect(productData: AdvancedPlansProductData): void;
  isSticky: boolean;
  rootStore: RootStore;
}

const ProductHeaderComp: React.FC<ProductHeaderProps> = ({
  product,
  onClickSelect,
  isSticky,
  rootStore,
  experiments,
}) => {
  const _product = React.useMemo(
    () => new AdvancedPlansProductModel(product, rootStore.appStore),
    [product],
  );
  const { t } = useTranslation();

  const {
    appContext: { isLimitedTimeDiscount, locale },
  } = rootStore.appStore;

  const isEnterpriseExperimentsEnabled = experiments.enabled(
    Experiments.AddEnterpriseProduct,
  );
  const { theme: themeName, isFontUpgradeActive } = usePageModeContext();

  const shouldReduceHeaderTitles = [Language.RU, Language.FR].includes(
    locale as Language,
  );

  return (
    <div
      data-testid='product-header'
      data-hook='product-header'
      className={classNames(
        s.productHeader,
        {
          [s.preselected]: _product.isSelected(),
          [s.productHeaderScaled]: isSticky,
          [s.reducedPadding]: shouldReduceHeaderTitles,
        },
        themeName,
      )}
    >
      {!isSticky && <ProductHat product={_product} />}
      <div className={s.product}>
        <div className={s.topSection}>
          <div className={s.productFamilyAndDescription}>
            <div data-testid='product-family' className={s.productFamily}>
              <Text
                weight='bold'
                className={classNames(s.planTitle, {
                  [s.smaller]: isEnterpriseExperimentsEnabled,
                  [s.tiny]: shouldReduceHeaderTitles,
                })}
              >
                {product.productName}
              </Text>
            </div>
            <div className={s.hideableContainer}>
              <div
                data-testid='product-description'
                className={s.productDescription}
              >
                <div className={s.productDescriptionLabel}>
                  <Text size='small'>{t(product.description)}</Text>
                </div>
              </div>
            </div>
          </div>
          <div className={s.hideableContainer}>
            <ProductNotes product={_product} />
          </div>
        </div>
        <div className={s.bottomSection}>
          {_product.isEnterprise() ? (
            <div
              className={classNames(s.enterprisePriceContainer, {
                [s.limitedTimeOffer]: isLimitedTimeDiscount,
                [s.enterpriseScaled]: isSticky,
              })}
              data-testid='enterprise-price'
            >
              <Text size='small'>
                {t('Advanced.Plans_Enterprise_product_price')}
              </Text>
            </div>
          ) : (
            <PriceWithDecimal
              className={classNames({ [s.priceScaled]: isSticky }, themeName)}
              size='small'
              product={_product}
              fontUpgrade={isFontUpgradeActive}
            />
          )}

          <LoggedIn>
            {(isLoggedIn) =>
              isLoggedIn && (
                <SelectButton
                  onClick={() => onClickSelect(product)}
                  isSticky={isSticky}
                  isSelected={_product.isSelected()}
                  isDisabled={!_product.isPurchaseEnabled()}
                  isCurrentPlanProduct={_product.isCurrentPlanProduct}
                >
                  {t(
                    _product.isEnterprise()
                      ? 'premium_plan.business_enterprise.request_call'
                      : 'productEntry.select',
                  )}
                </SelectButton>
              )
            }
          </LoggedIn>
        </div>
      </div>
    </div>
  );
};

export const ProductHeader = withExperiments(
  withInjectedRootStore(ProductHeaderComp),
);
